import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle,
    AlertCircle,
    RefreshCw,
    Copy,
    Check,
    ArrowLeft,
} from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@shared-frontend/components/ui/card";
import { Alert, AlertDescription } from "@shared-frontend/components/ui/alert";
import { Button } from "@shared-frontend/components/ui/button";
import type { AuthErrorInfo } from "../errors/auth-error-codes";
import { getErrorMessage } from "../errors/auth-error-messages";

interface AuthErrorDisplayProps {
    /** Structured error information */
    error: AuthErrorInfo;
    /** Callback when retry is clicked (only shown if error is retryable) */
    onRetry?: () => void | Promise<void>;
    /** Whether retry is in progress */
    isRetrying?: boolean;
    /** Whether to show auto-retry countdown for network errors */
    enableAutoRetry?: boolean;
    /** Auto-retry interval in seconds (default: 5) */
    autoRetryInterval?: number;
    /** Maximum auto-retry attempts (default: 3) */
    maxAutoRetries?: number;
    /** Whether to show the "Return to Login" button */
    showReturnToLogin?: boolean;
    /** Custom login path (default: /login) */
    loginPath?: string;
    /** Variant: "card" for full-page, "inline" for embedded in forms */
    variant?: "card" | "inline";
}

export function AuthErrorDisplay({
    error,
    onRetry,
    isRetrying = false,
    enableAutoRetry = false,
    autoRetryInterval = 5,
    maxAutoRetries = 3,
    showReturnToLogin = true,
    loginPath = "/login",
    variant = "card",
}: AuthErrorDisplayProps) {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [autoRetryCount, setAutoRetryCount] = useState(0);
    const [countdown, setCountdown] = useState<number | null>(null);

    const errorConfig = getErrorMessage(error.code);
    const canAutoRetry =
        enableAutoRetry && error.isRetryable && autoRetryCount < maxAutoRetries;

    // Handle copy reference ID
    const handleCopyReferenceId = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(error.referenceId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API not available
        }
    }, [error.referenceId]);

    // Handle return to login
    const handleReturnToLogin = useCallback(() => {
        navigate(loginPath);
    }, [navigate, loginPath]);

    // Handle manual retry
    const handleManualRetry = useCallback(() => {
        setAutoRetryCount(0);
        setCountdown(null);
        onRetry?.();
    }, [onRetry]);

    // Auto-retry countdown logic
    useEffect(() => {
        if (!canAutoRetry || !onRetry || isRetrying) return;

        setCountdown(autoRetryInterval);

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev === null || prev <= 1) {
                    clearInterval(timer);
                    return null;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [canAutoRetry, autoRetryInterval, onRetry, isRetrying, autoRetryCount]);

    // Trigger auto-retry when countdown reaches 0
    useEffect(() => {
        if (countdown === 0 && canAutoRetry && onRetry && !isRetrying) {
            setAutoRetryCount((prev) => prev + 1);
            onRetry();
        }
    }, [countdown, canAutoRetry, onRetry, isRetrying]);

    // Inline variant (for embedding in forms/alerts)
    if (variant === "inline") {
        return (
            <Alert variant="destructive">
                {error.isRetryable ? (
                    <AlertTriangle className="h-4 w-4" />
                ) : (
                    <AlertCircle className="h-4 w-4" />
                )}
                <AlertDescription className="flex items-center justify-between gap-4">
                    <div className="space-y-1">
                        <span className="font-medium">{errorConfig.title}</span>
                        <p className="text-sm opacity-90">{errorConfig.description}</p>
                    </div>
                    {error.isRetryable && onRetry && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleManualRetry}
                            disabled={isRetrying}
                            className="shrink-0"
                        >
                            {isRetrying ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : countdown !== null ? (
                                `Retry in ${countdown}s`
                            ) : (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Retry
                                </>
                            )}
                        </Button>
                    )}
                </AlertDescription>
            </Alert>
        );
    }

    // Card variant (for full-page error display)
    const isConnectionIssue = error.isRetryable;
    const IconComponent = isConnectionIssue ? AlertTriangle : AlertCircle;
    const iconColor = isConnectionIssue
        ? "text-amber-500"
        : "text-destructive";

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <IconComponent
                        className={`h-12 w-12 ${iconColor} mx-auto mb-4`}
                    />
                    <CardTitle className="text-2xl">
                        {errorConfig.title}
                    </CardTitle>
                    <CardDescription>
                        {errorConfig.description}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Suggestion */}
                    {errorConfig.suggestion && (
                        <p className="text-sm text-muted-foreground text-center">
                            {errorConfig.suggestion}
                        </p>
                    )}

                    {/* Reference ID pill */}
                    {error.referenceId && (
                        <div className="flex items-center justify-center">
                            <button
                                type="button"
                                onClick={handleCopyReferenceId}
                                className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted/80"
                            >
                                <span>Ref: <span className="font-mono">{error.referenceId.split("-").pop()}</span></span>
                                {copied ? (
                                    <Check className="h-3 w-3" />
                                ) : (
                                    <Copy className="h-3 w-3" />
                                )}
                            </button>
                        </div>
                    )}

                    {/* Auto-retry countdown */}
                    {canAutoRetry && countdown !== null && countdown > 0 && (
                        <p className="text-sm text-muted-foreground text-center">
                            Retrying automatically in {countdown} second
                            {countdown !== 1 ? "s" : ""}...
                        </p>
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-col gap-2">
                        {error.isRetryable && onRetry && (
                            <Button
                                onClick={handleManualRetry}
                                disabled={isRetrying}
                                className="w-full bg-foreground text-background hover:bg-foreground/90"
                            >
                                {isRetrying ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        Retrying...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Try Again
                                    </>
                                )}
                            </Button>
                        )}

                        {showReturnToLogin && (
                            <Button
                                variant="ghost"
                                onClick={handleReturnToLogin}
                                className="w-full"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Login
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
